import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Sticky add-to-cart bar — pinned to viewport bottom with page margins.
 * Teleports to document.body so no ancestor can trap position:fixed.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} content
 *
 * @extends {Component<Refs>}
 */
class StickyAddToCart extends Component {
  requiredRefs = ['content'];

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {boolean} */
  #forceVisible = false;

  /** @type {boolean} */
  #teleporting = false;

  /** @type {Map<Element, boolean>} */
  #buyButtonVisibility = new Map();

  connectedCallback() {
    super.connectedCallback();

    if (this.parentElement !== document.body) {
      this.#teleporting = true;
      document.body.appendChild(this);
      return;
    }

    this.#teleporting = false;
    this.#pinToViewport();
    this.#init();
  }

  disconnectedCallback() {
    if (this.#teleporting) return;

    super.disconnectedCallback?.();
    this.#abortController?.abort();
    this.#observer?.disconnect();
    this.#buyButtonVisibility.clear();
  }

  /** Inline pin so fixed bottom works even if stylesheet order fights us. */
  #pinToViewport() {
    this.style.setProperty('position', 'fixed');
    this.style.setProperty('top', 'auto');
    this.style.setProperty('right', 'var(--page-margin, 16px)');
    this.style.setProperty('bottom', 'var(--page-margin, 16px)');
    this.style.setProperty('left', 'var(--page-margin, 16px)');
    this.style.setProperty('width', 'auto');
    this.style.setProperty('max-width', 'none');
    this.style.setProperty('margin', '0');
    this.style.setProperty('z-index', '100');
  }

  #init() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    document.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    this.#observeBuyButtons();

    if (window.Shopify?.designMode) {
      this.#forceVisible = true;
      this.#setVisible(true);

      document.addEventListener('shopify:section:load', this.#onSectionLoad, { signal });
      document.addEventListener('shopify:section:unload', this.#onSectionUnload, { signal });
    }
  }

  #onSectionLoad = (event) => {
    if (event.target?.id !== `shopify-section-${this.dataset.sectionId}`) return;
    this.#forceVisible = true;
    this.#pinToViewport();
    this.#setVisible(true);
    this.#observeBuyButtons();
  };

  #onSectionUnload = (event) => {
    if (event.target?.id !== `shopify-section-${this.dataset.sectionId}`) return;
    this.remove();
  };

  #observeBuyButtons() {
    this.#observer?.disconnect();
    this.#buyButtonVisibility.clear();

    const productId = this.dataset.productId;
    if (!productId) return;

    const buyButtons = Array.from(
      document.querySelectorAll(`product-form-component[data-product-id="${productId}"]`)
    ).filter((el) => !this.contains(el));

    if (!buyButtons.length) {
      this.#setVisible(true);
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.#buyButtonVisibility.set(entry.target, entry.isIntersecting);
        }

        if (this.#forceVisible) {
          this.#setVisible(true);
          return;
        }

        const anyVisible = [...this.#buyButtonVisibility.values()].some(Boolean);
        this.#setVisible(!anyVisible);
      },
      {
        root: null,
        threshold: 0,
        rootMargin: '0px',
      }
    );

    for (const button of buyButtons) {
      this.#buyButtonVisibility.set(button, false);
      this.#observer.observe(button);
    }
  }

  /**
   * @param {boolean} visible
   */
  #setVisible(visible) {
    this.classList.toggle('is-visible', visible);
    this.toggleAttribute('inert', !visible);
    this.setAttribute('aria-hidden', visible ? 'false' : 'true');
  }

  #onVariantSelected = () => {
    const button = this.querySelector('[ref="addToCartButton"]');
    if (button instanceof HTMLButtonElement) button.disabled = true;
  };

  /**
   * @param {CustomEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = String(event.detail.data.newProduct.id);
    } else if (event.detail.data.productId && String(event.detail.data.productId) !== this.dataset.productId) {
      return;
    }

    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const newContent =
      event.detail.data.html.querySelector(`#shopify-section-${sectionId} [data-sticky-atc-content]`) ||
      event.detail.data.html.querySelector('[data-sticky-atc-content]');

    if (!(newContent instanceof HTMLElement) || !this.refs.content) return;

    const wasVisible = this.classList.contains('is-visible');
    morph(this.refs.content, newContent);

    this.#syncOptionsFromVariant(event.detail.resource);

    if (wasVisible || this.#forceVisible) {
      this.#setVisible(true);
    }

    this.#pinToViewport();
    this.updatedCallback?.();
    this.#observeBuyButtons();
  };

  /**
   * Fallback when morph leaves options empty.
   * @param {{ options?: string[], option1?: string, option2?: string, option3?: string } | null} variant
   */
  #syncOptionsFromVariant(variant) {
    const optionsEl = this.querySelector('[data-sticky-atc-options]');
    if (!optionsEl || !variant) return;
    if (optionsEl.textContent.trim()) {
      optionsEl.classList.remove('sticky-atc__options--empty');
      return;
    }

    const values = Array.isArray(variant.options)
      ? variant.options
      : [variant.option1, variant.option2, variant.option3].filter(Boolean);

    const line = values
      .filter((value) => value && String(value).toLowerCase() !== 'default title')
      .join(' | ');

    optionsEl.textContent = line;
    optionsEl.classList.toggle('sticky-atc__options--empty', line.length === 0);
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
